import React, { forwardRef, useEffect } from 'react';
import { BsChevronDown, BsChevronExpand, BsChevronUp } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSortBy, useTable } from 'react-table';
import { Box, Flex, Icon, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import equals from 'fast-deep-equal';
import PropTypes from 'prop-types';

import LoadingSpinner from '../Spinner';

function SortIcon({ column }) {
  if (column.disableSortBy) return null;
  return column.isSorted
    ? (column.isSortedDesc
      ? <Icon as={BsChevronDown} ml={1} />
      : <Icon as={BsChevronUp} ml={1} />)
    : <Icon as={BsChevronExpand} ml={1} />;
}

const MemoizedCell = React.memo(({ cell }) => (
  <Td
    colSpan={cell.column.colSpan || 2}
    isNumeric={cell.column.isNumeric}
    textAlign={cell.column.textAlign || 'left'}
  >
    {cell.render('cell')}
  </Td>
), (prev, next) => equals(prev.cell.value, next.cell.value));

const InfiniteScrollTable = forwardRef(({
  columns,
  data,
  hiddenColumns,
  height,
  hasMore,
  handleStateChange,
  fetchMoreData,
  isLoading,
  initialSortBy = [],
  backgroundComponent
}, ref) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { sortBy },
    setHiddenColumns
  } = useTable({
    columns,
    data: data || [],
    manualSortBy: true,
    autoResetSortBy: false,
    rowKey: 'id',
    initialState: {
      sortBy: initialSortBy
    }
  }, useSortBy);

  const BackgroundComponent = backgroundComponent || (() => <div />);

  useEffect(() => {
    handleStateChange({ sortBy });
  }, [handleStateChange, sortBy]);

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [isLoading, hiddenColumns, setHiddenColumns]);

  if (isLoading) {
    return (
      <LoadingSpinner isInline minHeight="200px" />
    );
  }

  return (
    <Box className="PaginatedTable" height={height} overflowX="auto" overflowY="auto" position="relative">
      <InfiniteScroll
        dataLength={data.length}
        hasMore={hasMore}
        height={height}
        loader={(<LoadingSpinner isInline />)}
        next={fetchMoreData}
        ref={ref}
        scrollableTarget="scrollableDiv"
        scrollThreshold={0.9}
      >
        <Table position="relative" variant="simple" {...getTableProps()} className="ReportContent">
          <Thead>
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => {
                  const headerProps = {
                    ...column.getSortByToggleProps(),
                    colSpan: column.colSpan || 2
                  };

                  return (
                    <Th
                      {...column.getHeaderProps(headerProps)}
                      bg="greyScale.100"
                      color="greyScale.900"
                      isNumeric={column.isNumeric}
                      position="sticky"
                      textAlign="center"
                      textStyle="bodySm600"
                      textTransform="none"
                      top={0}
                      zIndex={1}
                    >
                      <Flex alignItems="center" justifyContent={column.justifyContent || 'center'} whiteSpace="nowrap">
                        {column.render('header')}
                        <SortIcon column={column} />
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()} backgroundColor="white">
                  {row.cells.map(cell => (
                    <MemoizedCell cell={cell} {...cell.getCellProps()} />
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </InfiniteScroll>

      <Box
        left="50%"
        position="absolute"
        top="50%"
        transform="translate(-50%, -50%)"
      >
        <BackgroundComponent />
      </Box>
    </Box>
  );
});

InfiniteScrollTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object),
  fetchMoreData: PropTypes.func,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasMore: PropTypes.bool,
  handleStateChange: PropTypes.func,
  isLoading: PropTypes.bool,
  initialSortBy: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    desc: PropTypes.bool
  })),
  backgroundComponent: PropTypes.node
};

InfiniteScrollTable.defaultProps = {
  height: '800px',
  hiddenColumns: [],
  fetchMoreData: () => {},
  hasMore: false,
  isLoading: false,
  initialSortBy: [],
  columns: [],
  handleStateChange: () => {},
  backgroundComponent: null
};

export default InfiniteScrollTable;
